/** Factual owner classifications only: no tax/UC approval, browser persistence or auto-save. */
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import UCPayrollPreview from "./UCPayrollPreview";

const ROOT = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
const client = axios.create({ withCredentials: true, timeout: 20000 });
const URL = `${ROOT}/api/service-controls/universal_credit/setup/evidence-decisions`;
const HASH = /^[a-f0-9]{64}$/;
const DECISIONS = new Set(["reviewed", "needs_evidence"]);
const CLASSIFICATIONS = new Set(["business", "personal", "mixed", "transfer", "payroll", "unclear"]);

function validate(data, revision) {
  if (data?.setup_revision !== revision || data.active !== false || data.ready_to_submit !== false
      || data.classification_scope !== "factual_only"
      || !HASH.test(data.source_snapshot_sha256) || !Number.isInteger(data.review_revision)
      || data.review_revision < 0 || !Array.isArray(data.records) || !Array.isArray(data.decisions)
      || !data.factual_revisions || typeof data.factual_revisions !== "object"
      || typeof data.notice !== "string") throw new Error("Invalid evidence review");
  const keys = new Set();
  for (const row of data.records) {
    if (!HASH.test(row.record_key) || !HASH.test(row.record_fingerprint)
        || keys.has(row.record_key) || typeof row.date !== "string"
        || typeof row.kind !== "string"
        || !Number.isInteger(data.factual_revisions[row.record_key])
        || data.factual_revisions[row.record_key] < 0
        || (row.kind === "journey" ? !(row.miles === null || typeof row.miles === "string")
          : !(row.amount_pence === null || (Number.isSafeInteger(row.amount_pence) && row.amount_pence >= 0)))) {
      throw new Error("Invalid evidence record");
    }
    keys.add(row.record_key);
  }
  const reviewed = new Set();
  for (const item of data.decisions) {
    if (!keys.has(item.record_key) || reviewed.has(item.record_key)
        || !DECISIONS.has(item.decision) || !CLASSIFICATIONS.has(item.classification)
        || !(item.business_amount_pence === null || (Number.isSafeInteger(item.business_amount_pence) && item.business_amount_pence > 0))
        || typeof item.reason !== "string") {
      throw new Error("Invalid saved decision");
    }
    reviewed.add(item.record_key);
  }
  return data;
}

const poundsToPence = value => {
  if (!/^\d+(?:\.\d{1,2})?$/.test(value || "")) return null;
  const pence = Math.round(Number(value) * 100);
  return Number.isSafeInteger(pence) && pence > 0 ? pence : null;
};

export default function UCEvidenceReview({ setupRevision }) {
  const [view, setView] = useState(null);
  const [index, setIndex] = useState(0);
  const [decision, setDecision] = useState("needs_evidence");
  const [classification, setClassification] = useState("unclear");
  const [businessPart, setBusinessPart] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const lifecycle = useRef(0);
  const pending = useRef(false);
  useEffect(() => {
    lifecycle.current += 1;
    setView(null); setError(""); setMessage(""); setBusy(false); pending.current = false;
    return () => { lifecycle.current += 1; };
  }, [setupRevision]);
  const record = view?.records[index];
  useEffect(() => {
    const saved = view?.decisions.find(item => item.record_key === record?.record_key);
    setDecision(saved?.decision || "needs_evidence");
    setClassification(saved?.classification || "unclear");
    setBusinessPart(saved?.business_amount_pence ? (saved.business_amount_pence / 100).toFixed(2) : "");
    setReason(saved?.reason || "");
  }, [view, record?.record_key]);

  const load = async () => {
    if (pending.current) return;
    pending.current = true;
    const epoch = lifecycle.current;
    setBusy(true); setError(""); setMessage(""); setView(null);
    try {
      const { data } = await client.get(URL);
      const checked = validate(data, setupRevision);
      if (epoch === lifecycle.current) { setView(checked); setIndex(0); }
    } catch (_) {
      if (epoch === lifecycle.current) setError("Evidence could not be loaded. Refresh setup if access or dates changed.");
    } finally {
      if (epoch === lifecycle.current) { setBusy(false); pending.current = false; }
    }
  };

  const save = async event => {
    event.preventDefault();
    const businessAmount = classification === "mixed" ? poundsToPence(businessPart) : null;
    if (pending.current || !record || !reason.trim() || error || (classification === "mixed" && businessAmount === null)) return;
    pending.current = true;
    const epoch = lifecycle.current;
    const currentKey = record.record_key;
    setBusy(true); setMessage("");
    try {
      const next = { record_key: currentKey, decision,
        classification, business_amount_pence: businessAmount, reason: reason.trim() };
      const decisions = [...view.decisions.filter(item => item.record_key !== currentKey), next];
      const { data } = await client.put(URL, {
        request_id: crypto.randomUUID(), setup_revision: setupRevision,
        source_snapshot_sha256: view.source_snapshot_sha256,
        expected_review_revision: view.review_revision,
        expected_factual_revisions: Object.fromEntries(
          decisions.map(item => [item.record_key, view.factual_revisions[item.record_key]])
        ),
        decisions,
      }, { headers: { "x-ndg-service-control": "browser-v1" } });
      const checked = validate(data, setupRevision);
      if (epoch === lifecycle.current) {
        const answered = new Set(checked.decisions.map(item => item.record_key));
        const nextUnanswered = checked.records.findIndex(item => !answered.has(item.record_key));
        setView(checked);
        if (nextUnanswered >= 0) {
          setIndex(nextUnanswered);
          setMessage("Classification saved. Moving to the next unanswered record.");
        } else {
          setIndex(Math.max(0, checked.records.findIndex(item => item.record_key === currentKey)));
          setMessage("Classification saved. You have answered every record in this review queue.");
        }
      }
    } catch (_) {
      if (epoch === lifecycle.current) {
        setView(null);
        setError("Save was not confirmed. Reload evidence to check saved decisions before trying again.");
      }
    } finally {
      if (epoch === lifecycle.current) { setBusy(false); pending.current = false; }
    }
  };

  const classificationChanged = value => {
    setClassification(value);
    if (value === "unclear") setDecision("needs_evidence");
    if (value !== "mixed") setBusinessPart("");
  };

  return <div className="space-y-3 border-t pt-3" data-testid="uc-evidence-decisions">
    <button type="button" disabled={busy} onClick={load} className="min-h-11 px-4 py-2 border rounded">
      {busy ? "Working…" : view || error ? "Reload evidence" : "Review evidence records"}
    </button>
    {error && <p role="alert">{error}</p>}
    {message && <p role="status">{message}</p>}
    {view && <>
      <UCPayrollPreview key={`${setupRevision}:${view.source_snapshot_sha256}`} setupRevision={setupRevision} sourceSnapshot={view.source_snapshot_sha256} />
      <p className="text-sm">{view.notice}</p>
      <p className="text-sm">Assessment period: {view.period_start} to {view.period_end}</p>
      {!record && <p>No records are available to review. The missing-evidence checklist still applies.</p>}
      {record && <form onSubmit={save} className="space-y-2">
        <p className="font-semibold">Record {index + 1} of {view.records.length}: {record.kind.replaceAll("_", " ")}</p>
        <p>{record.date} · {record.kind === "journey" ? `${record.miles ?? "Unconfirmed"} miles`
          : record.amount_pence === null ? "Amount needs checking" : new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(record.amount_pence / 100)}</p>

        <label htmlFor="uc-evidence-classification" className="block">What is this record?</label>
        <select id="uc-evidence-classification" value={classification} disabled={busy}
          onChange={e => classificationChanged(e.target.value)}
          className="block w-full max-w-full p-3 border rounded bg-white text-slate-900 dark:bg-slate-800 dark:text-white">
          <option value="unclear">I’m not sure yet</option>
          <option value="business">Business</option>
          <option value="personal">Personal / not business</option>
          {record.kind !== "journey" && <option value="mixed">Mixed business and personal</option>}
          {record.kind !== "journey" && <option value="transfer">Transfer between accounts</option>}
          {record.kind !== "journey" && <option value="payroll">Payroll / salary related</option>}
        </select>

        {classification === "mixed" && <>
          <label htmlFor="uc-evidence-business-part" className="block">How much of this transaction was business?</label>
          <div className="flex items-center gap-2"><span>£</span><input id="uc-evidence-business-part" inputMode="decimal"
            value={businessPart} disabled={busy} onChange={e => setBusinessPart(e.target.value)} placeholder="0.00"
            className="block w-full max-w-full p-3 border rounded bg-white text-slate-900 dark:bg-slate-800 dark:text-white" /></div>
        </>}

        <label htmlFor="uc-evidence-decision" className="block">Review status</label>
        <select id="uc-evidence-decision" value={decision} disabled={busy || classification === "unclear"}
          onChange={e => setDecision(e.target.value)}
          className="block w-full max-w-full p-3 border rounded bg-white text-slate-900 dark:bg-slate-800 dark:text-white">
          <option value="needs_evidence">More evidence needed</option>
          <option value="reviewed">I have checked this classification</option>
        </select>
        <label htmlFor="uc-evidence-reason" className="block">What did you check, or what is missing?</label>
        <textarea id="uc-evidence-reason" required maxLength={1000} value={reason} disabled={busy}
          onChange={e => setReason(e.target.value)} className="block w-full max-w-full p-3 border rounded bg-white text-slate-900 dark:bg-slate-800 dark:text-white" />
        <button type="submit" disabled={busy || !reason.trim() || (classification === "mixed" && poundsToPence(businessPart) === null)}
          className="min-h-11 px-4 py-2 border rounded">Save review</button>
        <p className="text-sm">Saving moves to the next unanswered record. You can still return to earlier records with Previous.</p>
        <div className="flex flex-wrap gap-2">
          <button type="button" disabled={busy || index === 0} onClick={() => { setIndex(n => n - 1); setMessage(""); }} className="min-h-11 px-4 py-2 border rounded">Previous record</button>
          <button type="button" disabled={busy || index >= view.records.length - 1} onClick={() => { setIndex(n => n + 1); setMessage(""); }} className="min-h-11 px-4 py-2 border rounded">Next record</button>
        </div>
      </form>}
    </>}
  </div>;
}
